// App.js - Premium Red & White Theme with ASB Logo
import React, { useEffect, useState, Suspense, lazy } from "react";
import { HashRouter as Router, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaWhatsapp } from "react-icons/fa";

// Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import SearchResultsPage from './components/SearchResultsPage';

// Lazy loaded components
const HomePage = lazy(() => import("./components/HomePage"));
const ProductsPage = lazy(() => import("./components/ProductsPage"));
const ProductDetails = lazy(() => import("./components/ProductDetails"));
const CartPage = lazy(() => import("./components/CartPage"));
const AboutPage = lazy(() => import("./components/AboutUs"));
const ContactPage = lazy(() => import("./components/ContactUs"));
const StoreLocatorPage = lazy(() => import("./components/StoreLocatorPage"));
const LookbookPage = lazy(() => import("./components/LookbookPage"));
const SalePage = lazy(() => import("./components/SalePage"));
const AuthModal = lazy(() => import("./components/AuthModal"));
const NewArrivalsPage = lazy(() => import("./components/NewArrivalsPage"));
const FeaturedPage = lazy(() => import("./components/FeaturedPage"));
const CollectionPage = lazy(() => import("./components/CollectionPage"));
const WishlistPage = lazy(() => import("./components/WishlistPage"));
const GalleryPage = lazy(() => import("./components/GalleryPage"));
const EmployeeSupport = lazy(() => import("./components/EmployeeSupport"));
const GiftVoucher = lazy(() => import("./components/GiftVoucher"));
const KidsCategory = lazy(() => import("./components/KidsCategory"));
const MenCategory = lazy(() => import("./components/MenCategory"));
const WomenCategory = lazy(() => import("./components/WomenCategory"));
const TrendingNow = lazy(() => import("./components/TrendingNow"));
const PrivacyPolicy = lazy(() => import("./components/PrivacyPolicy"));
const TermsConditions = lazy(() => import("./components/TermsConditions"));
const Accessories = lazy(() => import("./components/Accessories"));
const TraditionalWear = lazy(() => import("./components/TraditionalWear")); 

// Simple Loader Component (minimal, no fancy animations)
const SimpleLoader = () => {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '300px',
      width: '100%'
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid #f0e0e0',
        borderTop: '3px solid #dc2626',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// Professional 404 Page - Red & White Theme
const NotFoundPage = () => {
  const [isMobile, setIsMobile] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        minHeight: 'calc(100vh - 200px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: isMobile ? '40px 20px' : '60px 40px',
        background: 'linear-gradient(135deg, #ffffff 0%, #fff5f5 100%)'
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: '500px' }}>
        <motion.div
          animate={{ y: [0, -10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div style={{
            width: isMobile ? '100px' : '140px',
            height: isMobile ? '100px' : '140px',
            background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 30px',
            boxShadow: '0 20px 40px rgba(220,38,38,0.2)'
          }}>
            <span style={{ fontSize: isMobile ? 50 : 70, color: 'white', fontWeight: 'bold' }}>404</span>
          </div>
        </motion.div>
        
        <motion.h1
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200 }}
          style={{ fontSize: isMobile ? '28px' : '32px', fontWeight: 'bold', color: '#dc2626', marginBottom: '16px' }}
        >
          Page Not Found
        </motion.h1>
        
        <p style={{ color: '#666', marginBottom: '32px', fontSize: isMobile ? '14px' : '16px' }}>
          The page you're looking for doesn't exist or has been moved.
        </p>
        
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => window.history.back()}
            style={{
              padding: isMobile ? '10px 24px' : '12px 32px',
              background: '#dc2626',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: isMobile ? '14px' : '16px'
            }}
          >
            Go Back
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate('/')}
            style={{
              padding: isMobile ? '10px 24px' : '12px 32px',
              background: 'transparent',
              color: '#dc2626',
              border: '2px solid #dc2626',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: isMobile ? '14px' : '16px'
            }}
          >
            Home Page
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

// Page Wrapper for CSS Management
const PageWrapper = ({ children, pageName }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pageName]);

  return children;
};

// Animated Routes Component
const AnimatedRoutes = () => {
  const location = useLocation();
  
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={
          <PageWrapper pageName="home">
            <Suspense fallback={<SimpleLoader />}>
              <HomePage />
            </Suspense>
          </PageWrapper>
        } />

        <Route path="/men" element={
          <PageWrapper pageName="men">
            <Suspense fallback={<SimpleLoader />}>
              <MenCategory />
            </Suspense>
          </PageWrapper>
        } />
        
        <Route path="/women" element={
          <PageWrapper pageName="women">
            <Suspense fallback={<SimpleLoader />}>
              <WomenCategory />
            </Suspense>
          </PageWrapper>
        } />
        
        <Route path="/kids" element={
          <PageWrapper pageName="kids">
            <Suspense fallback={<SimpleLoader />}>
              <KidsCategory />
            </Suspense>
          </PageWrapper>
        } />

        <Route path="/products" element={
          <PageWrapper pageName="products">
            <Suspense fallback={<SimpleLoader />}>
              <ProductsPage />
            </Suspense>
          </PageWrapper>
        } />

        <Route path="/product/:id" element={
          <PageWrapper pageName="product-details">
            <Suspense fallback={<SimpleLoader />}>
              <ProductDetails />
            </Suspense>
          </PageWrapper>
        } />
        
        <Route path="/trending-now" element={
          <PageWrapper pageName="trending">
            <Suspense fallback={<SimpleLoader />}>
              <TrendingNow />
            </Suspense>
          </PageWrapper>
        } />

        <Route path="/collections" element={
          <PageWrapper pageName="collections">
            <Suspense fallback={<SimpleLoader />}>
              <CollectionPage />
            </Suspense>
          </PageWrapper>
        } />

        <Route path="/cart" element={
          <PageWrapper pageName="cart">
            <Suspense fallback={<SimpleLoader />}>
              <CartPage />
            </Suspense>
          </PageWrapper>
        } />

        <Route path="/wishlist" element={
          <PageWrapper pageName="wishlist">
            <Suspense fallback={<SimpleLoader />}>
              <WishlistPage />
            </Suspense>
          </PageWrapper>
        } />

        <Route path="/about" element={
          <PageWrapper pageName="about">
            <Suspense fallback={<SimpleLoader />}>
              <AboutPage />
            </Suspense>
          </PageWrapper>
        } />

        <Route path="/contact" element={
          <PageWrapper pageName="contact">
            <Suspense fallback={<SimpleLoader />}>
              <ContactPage />
            </Suspense>
          </PageWrapper>
        } />
        <Route path="/search" element={<SearchResultsPage />} />
        <Route path="/terms" element={<TermsConditions />} />

        <Route path="/stores" element={
          <PageWrapper pageName="stores">
            <Suspense fallback={<SimpleLoader />}>
              <StoreLocatorPage />
            </Suspense>
          </PageWrapper>
        } />

        <Route path="/lookbook" element={
          <PageWrapper pageName="lookbook">
            <Suspense fallback={<SimpleLoader />}>
              <LookbookPage />
            </Suspense>
          </PageWrapper>
        } />

        <Route path="/sale" element={
          <PageWrapper pageName="sale">
            <Suspense fallback={<SimpleLoader />}>
              <SalePage />
            </Suspense>
          </PageWrapper>
        } />

        <Route path="/new-arrivals" element={
          <PageWrapper pageName="new-arrivals">
            <Suspense fallback={<SimpleLoader />}>
              <NewArrivalsPage />
            </Suspense>
          </PageWrapper>
        } />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/accessories" element={<Accessories />} />
        <Route path="/traditional" element={<TraditionalWear />} />

        <Route path="/featured" element={
          <PageWrapper pageName="featured">
            <Suspense fallback={<SimpleLoader />}>
              <FeaturedPage />
            </Suspense>
          </PageWrapper>
        } />

        <Route path="/gallery" element={
          <PageWrapper pageName="gallery">
            <Suspense fallback={<SimpleLoader />}>
              <GalleryPage />
            </Suspense>
          </PageWrapper>
        } />

        <Route path="/gift-voucher" element={
          <PageWrapper pageName="gift-voucher">
            <Suspense fallback={<SimpleLoader />}>
              <GiftVoucher />
            </Suspense>
          </PageWrapper>
        } />

        <Route path="/employee-support" element={
          <PageWrapper pageName="employee-support">
            <Suspense fallback={<SimpleLoader />}>
              <EmployeeSupport />
            </Suspense>
          </PageWrapper>
        } />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AnimatePresence>
  );
};

// Main App Component
function App() {
  const [user, setUser] = useState(null);
  const [appLoaded, setAppLoaded] = useState(false);
  const [showQuickHelp] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);

    const initializeApp = async () => {
      try {
        await new Promise(resolve => setTimeout(resolve, 800));
        
        const savedUser = localStorage.getItem('asb_user');
        if (savedUser) {
          setUser(JSON.parse(savedUser));
        }
        
        setAppLoaded(true);
      } catch (error) {
        console.error('App initialization error:', error);
        setAppLoaded(true);
      }
    };

    initializeApp();
    
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
    localStorage.setItem('asb_user', JSON.stringify(userData));
    setShowAuthModal(false);
    
    toast.success(`Welcome back, ${userData.name || 'Fashion Lover'}`);
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem('asb_user');
    toast.info(`See you soon`);
  };

  const handleAuth = (mode) => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };

  if (!appLoaded) {
    return <SimpleLoader />;
  }

  return (
    <Router>
      <div className="App">
        <Navbar 
          user={user}
          onAuth={handleAuth}
          onLogout={handleLogout}
        />
        
        <main className="asb-main-content">
          <AnimatedRoutes />
        </main>

        <Footer />

        {showAuthModal && (
          <Suspense fallback={null}>
            <AuthModal 
              mode={authMode}
              onClose={() => setShowAuthModal(false)}
              onLogin={handleLogin}
              onSwitchMode={setAuthMode}
            />
          </Suspense>
        )}

        {/* Quick Help Button */}
        {showQuickHelp && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            style={{
              position: 'fixed',
              bottom: isMobile ? '20px' : '30px',
              left: isMobile ? '20px' : '30px',
              zIndex: 1000
            }}
          >
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => window.open('https://wa.me/94719057057', '_blank')}
              style={{
                width: isMobile ? '48px' : '56px',
                height: isMobile ? '48px' : '56px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #25D366, #128C7E)',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 15px rgba(37,211,102,0.3)'
              }}
            >
              <FaWhatsapp size={isMobile ? 24 : 28} color="white" />
            </motion.button>
          </motion.div>
        )}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="light"
        />
      </div>

      <style>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
          overflow-x: hidden;
        }
        
        .asb-main-content {
          min-height: 100vh;
          position: relative;
        }
        
        /* Red Theme Scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        
        ::-webkit-scrollbar-thumb {
          background: #dc2626;
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: #b91c1c;
        }
        
        /* Red Theme Selection */
        ::selection {
          background: #dc2626;
          color: white;
        }
        
        .container-custom {
          width: 100%;
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 20px;
        }
        
        @media (max-width: 768px) {
          .container-custom {
            padding: 0 16px;
          }
        }
      `}</style>
    </Router>
  );
}

export default App;